import { supabase } from "@/integrations/supabase/client";
import { applySignedContainerRowRule, fetchUnzippedSignedContainerIds } from "./queries";

/**
 * Fetch projects for a building.
 */
export async function fetchProjects(buildingId: string) {
  const numericBuildingId = Number(buildingId);

  const { data, error } = await supabase
    .from("building_projects")
    .select("*")
    .eq("building_id", numericBuildingId)
    .eq("hidden", false)
    .eq("archived", false)
    .order("name");

  if (error) throw new Error(error.message);

  return (data ?? []).map((project) => ({
    id: String(project.id),
    building_id: String(project.building_id),
    name: project.name,
    description: project.description ?? null,
    created_at: project.created_at,
    created_by: String(project.created_by),
    modified_at: project.modified_at,
    modified_by: project.modified_by ? String(project.modified_by) : null,
    org_id: project.org_id,
    is_default: project.is_default,
  }));
}

/**
 * Fetch folders for a building, optionally scoped to a single project.
 */
export async function fetchFolders(buildingId: string, projectId?: string | null) {
  const numericBuildingId = Number(buildingId);
  const numericProjectId = projectId ? Number(projectId) : null;

  let query = supabase
    .from("document_folders")
    .select("*")
    .eq("building_id", numericBuildingId)
    .eq("is_deleted", false)
    .eq("hidden", false)
    .order("folder_path");
  if (numericProjectId != null) query = query.eq("project_id", numericProjectId);

  const { data, error } = await query;

  if (error) throw new Error(error.message);

  return (data ?? []).map((folder: any) => ({
    id: String(folder.id),
    // `name` is the authoritative label; `folder_path` is only a fallback for
    // legacy rows that predate the column. The DB's folder_path trigger is
    // BEFORE UPDATE and recomputes the path from the *pre-update* row, so a
    // renamed folder keeps its old path — deriving the name from the path
    // showed the pre-rename name everywhere the store feeds (row breadcrumbs,
    // folder tag chips) until the user reloaded, and even then it stayed stale.
    name: folder.name || (folder.folder_path || "").split("/").filter(Boolean).pop() || "",
    parentId: folder.parent_id ? String(folder.parent_id) : null,
    category: (folder.folder_config?.category as any) || "documents",
    isCustom: !!folder.is_custom,
    building_id: folder.building_id,
    project_id: folder.project_id || null,
    folder_path: folder.folder_path,
    folder_depth: folder.folder_depth,
    is_project_scoped: folder.is_project_scoped,
  }));
}

/**
 * Fetch unique custom tags for a building, optionally scoped to a single project.
 */
export async function fetchCustomTags(buildingId: string, projectId?: string | null) {
  const numericBuildingId = Number(buildingId);
  const numericProjectId = projectId ? Number(projectId) : null;

  // Paginate to avoid Supabase's default 1000-row PostgREST limit
  const PAGE_SIZE = 1000;
  const allTags = new Set<string>();
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    let query = supabase
      .from("documents")
      .select("*, document_document_categories(document_categories(*))")
      .eq("building_id", numericBuildingId)
      .eq("hidden", false)
      .range(offset, offset + PAGE_SIZE - 1);
    if (numericProjectId != null) query = query.eq("project_id", numericProjectId);

    const { data, error } = await query;

    if (error) throw new Error(error.message);

    const page = data ?? [];
    page.forEach((doc: any) => {
      const categories = doc.document_document_categories || [];
      categories.forEach((catRel: any) => {
        if (catRel.document_categories?.category_name) {
          allTags.add(catRel.document_categories.category_name);
        }
      });
    });

    if (page.length < PAGE_SIZE) {
      hasMore = false;
    } else {
      offset += PAGE_SIZE;
    }
  }

  return Array.from(allTags).sort();
}

// Filter types
export interface FilterCategory {
  id: number;
  name: string;
  name_et: string | null;
  count: number;
  /** Inclusive count: direct documents + all subcategory documents */
  inclusiveCount: number;
  /** Parent category ID from DB (null = primary/top-level category) */
  parentCategoryId: number | null;
  /** Whether this is a primary category (parent_category_id = NULL) */
  isPrimary: boolean;
  /** IDs of direct child categories (subcategories) */
  childCategoryIds: number[];
}

export interface FilterDiscipline {
  id: number;
  name: string;
  name_en: string | null;
  description: string | null;
  count: number;
}

export interface FilterStage {
  id: number;
  name: string;
  name_en: string | null;
  count: number;
}

export interface FilterProject {
  id: number;
  count: number;
}

/** Filter group item for display in filter UI */
export interface FilterGroupItemDisplay {
  id: number;
  filterGroupId: number;
  documentCategoryId: number;
  categoryName: string;
  categoryNameEt: string | null;
  count: number;
}

/** Filter group for display in filter UI */
export interface FilterGroupDisplay {
  id: number;
  label: string;
  labelEt?: string | null;
  linkedCategoryId: number | null;
  count: number;
  items: FilterGroupItemDisplay[];
}

export interface AvailableFilters {
  projectIds: number[];
  projects: FilterProject[];
  categories: FilterCategory[];
  /** Filter groups for the decoupled filter hierarchy UI */
  filterGroups: FilterGroupDisplay[];
  designDisciplines: FilterDiscipline[];
  designStages: FilterStage[];
}

/**
 * Fetch all available filter values that have at least one document in the building.
 */
export async function fetchAvailableFilters(
  buildingId: string,
  projectId?: string
): Promise<AvailableFilters> {
  const numericBuildingId = Number(buildingId);

  const signedContainerIds = await fetchUnzippedSignedContainerIds(numericBuildingId);

  // Paginate through all results to avoid Supabase's default 1000-row PostgREST limit
  const PAGE_SIZE = 1000;
  const allData: any[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    let query = supabase
      .from("documents")
      .select(
        `
        project_id,
        document_document_categories(
          document_categories(id, category_name, name_et, parent_category_id)
        ),
        document_design_disciplines(
          design_disciplines(id, name, name_en, description)
        ),
        document_design_stages(
          design_stages(id, name, name_en)
        )
      `
      )
      .eq("building_id", numericBuildingId)
      .eq("is_deleted", false)
      .eq("is_latest_version", true)
      .eq("hidden", false)
      // Exclude DWG child documents (converted PDFs from DWG files)
      .or("is_dwg_child.is.null,is_dwg_child.eq.false")
      .range(offset, offset + PAGE_SIZE - 1);
    // Same row rule as listDocuments — a tag count here must agree with the
    // rows the filter would actually surface.
    query = applySignedContainerRowRule(query, signedContainerIds);

    if (projectId) {
      query = query.eq("project_id", Number(projectId));
    }

    const { data, error } = await query;

    if (error) throw new Error(error.message);

    const page = data ?? [];
    allData.push(...page);

    if (page.length < PAGE_SIZE) {
      hasMore = false;
    } else {
      offset += PAGE_SIZE;
    }
  }

  const projectIdsMap = new Map<number, number>();
  // Store direct counts and parent info for categories
  const categoriesMap = new Map<
    number,
    {
      id: number;
      name: string;
      name_et: string | null;
      directCount: number;
      parentCategoryId: number | null;
    }
  >();
  // Track unique category IDs per document for inclusive (subtree) counting
  const documentCategoryIds: number[][] = [];
  const disciplinesMap = new Map<number, FilterDiscipline & { count: number }>();
  const stagesMap = new Map<number, FilterStage & { count: number }>();

  for (const doc of allData) {
    if (doc.project_id) {
      projectIdsMap.set(doc.project_id, (projectIdsMap.get(doc.project_id) ?? 0) + 1);
    }

    const categories = (doc as any).document_document_categories ?? [];
    const docCategoryIdSet = new Set<number>();
    for (const catRel of categories) {
      const cat = catRel.document_categories;
      if (cat?.id && cat?.category_name) {
        docCategoryIdSet.add(cat.id);

        // Ensure category exists in map with parent info (directCount incremented below)
        if (!categoriesMap.has(cat.id)) {
          categoriesMap.set(cat.id, {
            id: cat.id,
            name: cat.category_name,
            name_et: cat.name_et ?? null,
            directCount: 0,
            parentCategoryId: cat.parent_category_id ?? null,
          });
        }
      }
    }

    // Increment directCount once per category per document
    for (const catId of docCategoryIdSet) {
      const existing = categoriesMap.get(catId);
      if (existing) {
        existing.directCount += 1;
      }
    }

    if (docCategoryIdSet.size > 0) {
      documentCategoryIds.push(Array.from(docCategoryIdSet));
    }

    const disciplines = (doc as any).document_design_disciplines ?? [];
    for (const discRel of disciplines) {
      const disc = discRel.design_disciplines;
      if (disc?.id && disc?.name) {
        const existing = disciplinesMap.get(disc.id);
        if (existing) {
          existing.count += 1;
        } else {
          disciplinesMap.set(disc.id, {
            id: disc.id,
            name: disc.name,
            name_en: disc.name_en ?? null,
            description: disc.description ?? null,
            count: 1,
          });
        }
      }
    }

    const stages = (doc as any).document_design_stages ?? [];
    for (const stageRel of stages) {
      const stage = stageRel.design_stages;
      if (stage?.id && stage?.name) {
        const existing = stagesMap.get(stage.id);
        if (existing) {
          existing.count += 1;
        } else {
          stagesMap.set(stage.id, {
            id: stage.id,
            name: stage.name,
            name_en: stage.name_en ?? null,
            count: 1,
          });
        }
      }
    }
  }

  // Fetch missing parent categories that aren't in our data
  // This ensures we have hierarchy info even when parent has no direct documents
  const missingParentIds = new Set<number>();
  for (const cat of categoriesMap.values()) {
    if (cat.parentCategoryId !== null && !categoriesMap.has(cat.parentCategoryId)) {
      missingParentIds.add(cat.parentCategoryId);
    }
  }

  if (missingParentIds.size > 0) {
    const { data: parentCategories, error: parentError } = await supabase
      .from("document_categories")
      .select("id, category_name, name_et, parent_category_id")
      .in("id", Array.from(missingParentIds));

    if (!parentError && parentCategories) {
      for (const parent of parentCategories) {
        if (!categoriesMap.has(parent.id)) {
          categoriesMap.set(parent.id, {
            id: parent.id,
            name: parent.category_name,
            name_et: parent.name_et ?? null,
            directCount: 0, // No direct documents
            parentCategoryId: parent.parent_category_id ?? null,
          });
        }
      }
    }
  }

  // Build parent-child relationships for categories
  const childrenMap = new Map<number, number[]>();
  for (const [catId, cat] of categoriesMap) {
    if (cat.parentCategoryId !== null) {
      const siblings = childrenMap.get(cat.parentCategoryId) || [];
      siblings.push(catId);
      childrenMap.set(cat.parentCategoryId, siblings);
    }
  }

  // Compute inclusive counts as UNIQUE documents per category subtree.
  //
  // Goal: for each category, show how many documents belong to that category OR any of its descendants.
  // A single document can carry multiple tags within the same subtree (e.g. both a parent and a
  // subcategory), so we must dedupe per-document to avoid double-counting.
  //
  // Why per-document (not per-category)?
  // - We want a doc-count, not a tag-occurrence count. Iterating documents makes it easy to ensure
  //   each document increments a category at most once.
  //
  // Why walk UP the tree (not down)?
  // - Each assigned category implies membership in all its ancestors. We already have parent pointers,
  //   so walking up is the simplest way to mark all categories a document should count toward.
  //
  // Example:
  // - Document tagged [Inspection Report, Report] should increment Report only once.
  // - `visitedForDoc` enforces that invariant.
  //
  // Optimization:
  // - Cache each category's ancestor chain so we don't re-walk parent pointers for every document.
  const inclusiveCountsMap = new Map<number, number>();
  const ancestorChainByCategoryId = new Map<number, number[]>();

  function getAncestorChain(catId: number): number[] {
    const cached = ancestorChainByCategoryId.get(catId);
    if (cached) return cached;

    const chain: number[] = [];
    const seen = new Set<number>();
    let currentId: number | null | undefined = catId;

    while (currentId != null && !seen.has(currentId)) {
      const cat = categoriesMap.get(currentId);
      if (!cat) break;

      seen.add(currentId);
      chain.push(currentId);
      currentId = cat.parentCategoryId;
    }

    ancestorChainByCategoryId.set(catId, chain);
    return chain;
  }

  for (const docCats of documentCategoryIds) {
    const visitedForDoc = new Set<number>();

    for (const catId of docCats) {
      for (const ancestorId of getAncestorChain(catId)) {
        visitedForDoc.add(ancestorId);
      }
    }

    for (const visitedCatId of visitedForDoc) {
      inclusiveCountsMap.set(visitedCatId, (inclusiveCountsMap.get(visitedCatId) ?? 0) + 1);
    }
  }

  // Build final FilterCategory array with hierarchy info
  const categories: FilterCategory[] = Array.from(categoriesMap.values()).map((cat) => ({
    id: cat.id,
    name: cat.name,
    name_et: cat.name_et,
    count: cat.directCount,
    inclusiveCount: inclusiveCountsMap.get(cat.id) ?? 0,
    parentCategoryId: cat.parentCategoryId,
    isPrimary: cat.parentCategoryId === null,
    childCategoryIds: childrenMap.get(cat.id) || [],
  }));

  const projects: FilterProject[] = Array.from(projectIdsMap.entries())
    .map(([id, count]) => ({ id, count }))
    .sort((a, b) => a.id - b.id);

  // Fetch filter groups and items for the decoupled filter hierarchy
  const filterGroups = await fetchFilterGroupsWithCounts(categoriesMap, inclusiveCountsMap);

  return {
    projectIds: Array.from(projectIdsMap.keys()).sort((a, b) => a - b),
    projects,
    categories: categories.sort((a, b) => a.name.localeCompare(b.name)),
    filterGroups,
    designDisciplines: Array.from(disciplinesMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    ),
    designStages: Array.from(stagesMap.values()).sort((a, b) => a.name.localeCompare(b.name)),
  };
}

/**
 * Fetch filter groups with their items and compute counts.
 * Count for a filter group = unique documents matching linked category OR any item category.
 * Count for an item = documents matching that specific category.
 */
async function fetchFilterGroupsWithCounts(
  categoriesMap: Map<
    number,
    {
      id: number;
      name: string;
      name_et: string | null;
      directCount: number;
      parentCategoryId: number | null;
    }
  >,
  inclusiveCountsMap: Map<number, number>
): Promise<FilterGroupDisplay[]> {
  // Fetch all filter groups
  const { data: groupsData, error: groupsError } = await supabase
    .from("document_filter_groups")
    .select("id, label, label_et, linked_category_id")
    .order("label", { ascending: true });

  if (groupsError) {
    console.error("Error fetching filter groups:", groupsError);
    return [];
  }

  // Fetch all filter group items with category names
  const { data: itemsData, error: itemsError } = await supabase
    .from("document_filter_group_items")
    .select(
      `
      id,
      filter_group_id,
      document_category_id,
      document_categories!inner (
        category_name,
        name_et
      )
    `
    );

  if (itemsError) {
    console.error("Error fetching filter group items:", itemsError);
    return [];
  }

  // Group items by filter_group_id
  const itemsByGroupId = new Map<
    number,
    Array<{
      id: number;
      document_category_id: number;
      category_name: string;
      category_name_et: string | null;
    }>
  >();

  for (const item of itemsData ?? []) {
    const groupItems = itemsByGroupId.get(item.filter_group_id) ?? [];
    const catData = item.document_categories as { category_name: string; name_et: string | null };
    groupItems.push({
      id: item.id,
      document_category_id: item.document_category_id,
      category_name: catData?.category_name ?? "",
      category_name_et: catData?.name_et ?? null,
    });
    itemsByGroupId.set(item.filter_group_id, groupItems);
  }

  // For groups with linked_category_id but no items, inject the linked category
  // as a synthetic item so the group appears in the filter sidebar.
  const emptyGroupCategoryIds: number[] = [];
  for (const group of groupsData ?? []) {
    if (group.linked_category_id && !itemsByGroupId.get(group.id)?.length) {
      emptyGroupCategoryIds.push(group.linked_category_id);
    }
  }

  const linkedCategoryNames = new Map<number, { name: string; name_et: string | null }>();
  if (emptyGroupCategoryIds.length > 0) {
    const { data: catData } = await supabase
      .from("document_categories")
      .select("id, category_name, name_et")
      .in("id", emptyGroupCategoryIds);
    for (const cat of catData ?? []) {
      linkedCategoryNames.set(cat.id, { name: cat.category_name, name_et: cat.name_et ?? null });
    }
  }

  // Build FilterGroupDisplay objects
  const filterGroups: FilterGroupDisplay[] = [];

  for (const group of groupsData ?? []) {
    const items = itemsByGroupId.get(group.id) ?? [];

    // Inject linked category as synthetic item when group has no items
    if (items.length === 0 && group.linked_category_id) {
      const catInfo = linkedCategoryNames.get(group.linked_category_id);
      if (catInfo) {
        items.push({
          id: -group.id,
          document_category_id: group.linked_category_id,
          category_name: catInfo.name,
          category_name_et: catInfo.name_et,
        });
      }
    }

    // Sort items alphabetically by category name
    items.sort((a, b) => a.category_name.localeCompare(b.category_name));

    // Compute item counts (direct count for each category)
    const displayItems: FilterGroupItemDisplay[] = items.map((item) => ({
      id: item.id,
      filterGroupId: group.id,
      documentCategoryId: item.document_category_id,
      categoryName: item.category_name,
      categoryNameEt: item.category_name_et,
      // Use inclusive count to include subcategory documents
      count: inclusiveCountsMap.get(item.document_category_id) ?? 0,
    }));

    // Compute group count = unique documents with linked category OR any item category
    // We use inclusive counts for a more accurate representation
    const allCategoryIds = new Set<number>();
    if (group.linked_category_id) {
      allCategoryIds.add(group.linked_category_id);
    }
    for (const item of items) {
      allCategoryIds.add(item.document_category_id);
    }

    // For group count, use the max of inclusive counts (approximation)
    // A more accurate count would require re-querying documents, but this is a reasonable estimate
    let groupCount = 0;
    if (group.linked_category_id) {
      groupCount = inclusiveCountsMap.get(group.linked_category_id) ?? 0;
    }
    // If no linked category, sum all item counts (may overcount but better than zero)
    if (groupCount === 0 && displayItems.length > 0) {
      // Use the count from all items combined (unique estimate from item counts)
      groupCount = displayItems.reduce((sum, item) => sum + item.count, 0);
    }

    filterGroups.push({
      id: group.id,
      label: group.label,
      labelEt: group.label_et,
      linkedCategoryId: group.linked_category_id,
      count: groupCount,
      items: displayItems,
    });
  }

  return filterGroups;
}

/**
 * Head-count of the documents a user would actually see, for one building and
 * optionally one project.
 *
 * Same row rule as listDocuments — the count must agree with the rendered rows:
 * the signed container counts as one, its enclosed documents as none.
 */
async function countVisibleDocuments(
  buildingId: string,
  projectId?: string,
  createdSince?: string
): Promise<number> {
  const numericBuildingId = Number(buildingId);
  const signedContainerIds = await fetchUnzippedSignedContainerIds(numericBuildingId);

  let query = supabase
    .from("documents")
    .select("id", { count: "exact", head: true })
    .eq("building_id", numericBuildingId)
    .eq("is_deleted", false)
    .eq("is_latest_version", true)
    .eq("hidden", false)
    // Exclude DWG child documents (converted PDFs from DWG files)
    .or("is_dwg_child.is.null,is_dwg_child.eq.false");
  query = applySignedContainerRowRule(query, signedContainerIds);

  if (projectId) {
    query = query.eq("project_id", Number(projectId));
  }
  if (createdSince) {
    query = query.gte("created_at", createdSince);
  }

  const { count, error } = await query;

  if (error) throw new Error(error.message);

  return count ?? 0;
}

/**
 * Fetch total document count for a building, optionally filtered by project.
 */
export async function fetchDocumentCount(
  buildingId: string,
  projectId?: string
): Promise<{ count: number }> {
  return { count: await countVisibleDocuments(buildingId, projectId) };
}

/**
 * How many documents landed in the scope since `sinceIso` — the "what's new"
 * signal behind the chat's recent-upload suggestions. Counts the same rows the
 * Documents page would show, so the number matches what the user can go and open.
 */
export async function countDocumentsCreatedSince(
  buildingId: string,
  sinceIso: string,
  projectId?: string
): Promise<number> {
  return countVisibleDocuments(buildingId, projectId, sinceIso);
}

export interface FolderDocumentCount {
  folderId: string;
  name: string;
  parentId: string | null;
  folderPath: string | null;
  directCount: number;
  totalCount: number;
}

/**
 * Fetch document counts for all folders in a project, with recursive totals.
 *
 * Also reports `unreachableDocIds`: visible documents whose `folder_id` names
 * no live folder of this project. Such a row can be rendered nowhere in the
 * Folders tab — it is not at the root (`folder_id` is set) and its folder is
 * not a node in the tree — so without this it is simply lost from that view
 * while List and Timeline keep showing it. Both sides are already read here, so
 * the answer costs nothing extra.
 */
export async function fetchFolderDocumentCounts(
  projectId: string,
  buildingId: string
): Promise<{
  folders: Map<string, FolderDocumentCount>;
  rootCount: number;
  unreachableDocIds: string[];
}> {
  const numericProjectId = Number(projectId);
  const numericBuildingId = Number(buildingId);

  // Same row rule as listDocuments — a folder badge must agree with the rows
  // the folder renders: one for the signed container, none for its contents.
  const signedContainerIds = await fetchUnzippedSignedContainerIds(numericBuildingId);

  // Paginate through all results to avoid Supabase's default 1000-row PostgREST limit
  const PAGE_SIZE = 1000;
  const allDocs: { id: number; folder_id: number | null }[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    let pageQuery = supabase
      .from("documents")
      .select("id, folder_id")
      .eq("project_id", numericProjectId)
      .eq("building_id", numericBuildingId)
      .eq("is_deleted", false)
      .eq("is_latest_version", true)
      .eq("hidden", false)
      // Exclude DWG child documents (converted PDFs from DWG files)
      .or("is_dwg_child.is.null,is_dwg_child.eq.false")
      // Range paging over an unordered result may skip or repeat rows between
      // pages. A skipped row is a document silently left unreachable, since
      // this scan is what finds them.
      .order("id");
    pageQuery = applySignedContainerRowRule(pageQuery, signedContainerIds);
    const { data: docsData, error: docsError } = await pageQuery.range(
      offset,
      offset + PAGE_SIZE - 1
    );

    if (docsError) throw new Error(docsError.message);

    const page = docsData ?? [];
    allDocs.push(...page);

    if (page.length < PAGE_SIZE) {
      hasMore = false;
    } else {
      offset += PAGE_SIZE;
    }
  }

  const directCounts = new Map<string, number>();
  let rootCount = 0;

  for (const doc of allDocs) {
    if (doc.folder_id) {
      const folderId = String(doc.folder_id);
      directCounts.set(folderId, (directCounts.get(folderId) || 0) + 1);
    } else {
      rootCount++;
    }
  }

  // Paged for the same reason as the documents above: truncated at 1000 rows
  // this map would be missing folders, and every document inside one of them
  // would then be reported unreachable and duplicated into the root list.
  const foldersData: {
    id: number;
    name: string | null;
    parent_id: number | null;
    folder_path: string;
  }[] = [];
  for (let folderOffset = 0; ; folderOffset += PAGE_SIZE) {
    const { data, error: foldersError } = await supabase
      .from("document_folders")
      .select("id, name, parent_id, folder_path")
      .eq("project_id", numericProjectId)
      .eq("building_id", numericBuildingId)
      .eq("is_deleted", false)
      .order("id")
      .range(folderOffset, folderOffset + PAGE_SIZE - 1);

    if (foldersError) throw new Error(foldersError.message);
    const page = data ?? [];
    foldersData.push(...page);
    if (page.length < PAGE_SIZE) break;
  }

  const folderMap = new Map<string, FolderDocumentCount>();
  for (const folder of foldersData) {
    const folderId = String(folder.id);
    folderMap.set(folderId, {
      folderId,
      name: folder.name || "",
      parentId: folder.parent_id ? String(folder.parent_id) : null,
      folderPath: folder.folder_path,
      directCount: directCounts.get(folderId) || 0,
      totalCount: directCounts.get(folderId) || 0,
    });
  }

  const childrenMap = new Map<string | null, string[]>();
  for (const [folderId, folder] of folderMap) {
    const parentId = folder.parentId;
    if (!childrenMap.has(parentId)) {
      childrenMap.set(parentId, []);
    }
    childrenMap.get(parentId)!.push(folderId);
  }

  function computeTotalCount(folderId: string): number {
    const folder = folderMap.get(folderId);
    if (!folder) return 0;

    const children = childrenMap.get(folderId) || [];
    let total = folder.directCount;

    for (const childId of children) {
      total += computeTotalCount(childId);
    }

    folder.totalCount = total;
    return total;
  }

  const rootFolders = childrenMap.get(null) || [];
  for (const folderId of rootFolders) {
    computeTotalCount(folderId);
  }

  // `folderMap` holds exactly the folders the tree renders (same building +
  // project + not-deleted scope as folderService.getProjectFolders), so a
  // document pointing outside it has no home in the Folders tab. Seen in real
  // data as documents moved to another project while keeping the source
  // project's folder_id, and reachable again once they are listed somewhere.
  const unreachableDocIds: string[] = [];
  for (const doc of allDocs) {
    if (doc.folder_id && !folderMap.has(String(doc.folder_id))) {
      unreachableDocIds.push(String(doc.id));
    }
  }

  return { folders: folderMap, rootCount, unreachableDocIds };
}
